import React from 'react';

import cloneElementWithProps from '../core/cloneElementWithProps';

import type { InjectedRouter, PlainRoute, RouterState } from 'react-router';

/**
 * When to use LocationContext vs RouterContext.location:
 *
 * - Use RouterContext.location if you need the current value of the address bar.
 *   Eg, using the address bar as state and need the value to update immediately.
 *
 * - Use LocationContext if you need the location of the currently rendered page. This location may
 *   not match the address bar when page content has changed.
 *   Eg, using the current path to generate a link.
 *
 * Why are they different?
 * - When the url changes, react-router will attempt to render the new DOM tree.
 * - If the new DOM tree contains an `asyncConnect` function redux-connect prevents the route from
 *   changing by preserving the previous value of `location` until the promise resolves.
 * - When the `asyncConnect` resolves, redux-connect will pass the route new `location`,
 *   allowing the new page to render.
 */
export const LocationContext = React.createContext<RouterState['location']>({} as any);
export const ParamsContext = React.createContext<RouterState['params']>({} as any);
export const RouterContext = React.createContext<InjectedRouter>({} as any);
export const RouteContext = React.createContext<PlainRoute>({} as any);

// Should use RouteComponentProps, but this is simpler
interface Props {
  location: RouterState['location'];
  params: RouterState['params'];
  route: PlainRoute;
  router: InjectedRouter;
  children: React.ReactNode;
}

export default function RouterContainer({
  location,
  params,
  route,
  router,
  children,
  ...props
}: Props) {
  return (
    <LocationContext.Provider value={location}>
      <ParamsContext.Provider value={params}>
        <RouterContext.Provider value={router}>
          <RouteContext.Provider value={route}>
            {cloneElementWithProps(children, { ...props })}
          </RouteContext.Provider>
        </RouterContext.Provider>
      </ParamsContext.Provider>
    </LocationContext.Provider>
  );
}
